import { INVENTORY_ACTIONS, POSITION_ACTIONS, STATS_ACTIONS } from "../Actions/utilsActions";
import { ITEMS_LIST } from "../../Classes/utils/constants";
import { MAPS } from "../../Helpers/maps";
import { STATS_UPDATE_TYPE } from "../../Helpers/constants";
import { log } from "../../Helpers/logger";

/**
 * État initial pour la position du héros
 * @type {{x: number, y: number}}
 */
const INITIAL_POSITION_STATE = {x: 0, y: 0};

/**
 * État initial pour les statistiques du héros
 * @type {{health: number, maxHealth: number, mana: number, maxMana: number, strength: number, intelligence: number}}
 */
const INITIAL_STATE_STATE = {
    health: 0, 
    maxHealth: 0, 
    mana: 0, 
    maxMana: 0, 
    strength: 0, 
    intelligence: 0
};

// ======================================================================================

// ----------------------------------------------------------------------------
// ----------------------------------------------------------------------------
/**
 * Réducteur pour gérer la position du héros
 * @param {Object} state - État actuel de la position
 * @param {Object} action - Action à traiter
 * @returns {Object} Nouvel état de la position
 */
export const positionReducer = (state = INITIAL_POSITION_STATE, action = {}) => {
    log.reducer.log('positionReducer - Action reçue:', action.type, 'avec payload:', action.payload, 'état actuel:', state);
    
    // Si l'action n'est pas définie, on retourne l'état actuel
    if (!action || typeof action !== 'object') {
        log.reducer.log('positionReducer - Action invalide, retour de l\'état actuel');
        return state;
    }
    
    // Gestion du cas par défaut pour les actions non gérées
    if (!action.type) {
        log.reducer.log('positionReducer - Type d\'action manquant, retour de l\'état actuel');
        return state;
    }
    
    try {
        switch (action.type) {
            case POSITION_ACTIONS.MOVE: {
                const { x, y } = action.payload;
                
                // Validation des entrées
                if (typeof x !== 'number' || typeof y !== 'number' || isNaN(x) || isNaN(y)) {
                    console.error('Invalid position values:', { x, y });
                    return state;
                }
                
                // Récupérer les dimensions de la carte actuelle
                const mapId = state.mapId || 'map1'; // Changed from 'mapLarge' to 'map1'
                const currentMap = MAPS[mapId];
                
                if (!currentMap) {
                    console.error('Map not found. Available maps:', Object.keys(MAPS));
                    return state;
                }
                
                // Calcul de la nouvelle position
                let newX = (state.x || 0) + x;
                let newY = (state.y || 0) + y;
                
                // Vérifier les limites de la carte
                newX = Math.max(0, Math.min(newX, (currentMap.columns - 1) || 0));
                newY = Math.max(0, Math.min(newY, (currentMap.rows - 1) || 0));
                
                // Si la position n'a pas changé, on retourne l'état actuel
                if (newX === state.x && newY === state.y) {
                    log.reducer.log('positionReducer - Position inchangée, retour de l\'état actuel');
                    return state;
                }
                
                const newState = { 
                    ...state,
                    x: newX,
                    y: newY,
                    mapId: mapId // S'assurer que mapId est préservé
                };
                
                log.reducer.log('positionReducer - Nouvelle position:', newState);
                return newState;
            }
            default:
                return state;
        }
    } catch (error) {
        console.error('Error in positionReducer:', error);
        return state;
    }
}

// ----------------------------------------------------------------------------
/**
 * Réducteur pour gérer les statistiques du héros
 * @param {Object} state - État actuel des statistiques
 * @param {Object} action - Action à traiter
 * @returns {Object} Nouvel état des statistiques
 */
export const statsReducer = (state = INITIAL_STATE_STATE, action = {}) => {
    log.reducer.log("statsReducer - Action reçue:", action.type, action);
    
    switch (action.type) {
        case STATS_ACTIONS.UPDATE: {
            try {
                const { payload } = action;
                
                // Validation du payload
                if (!Array.isArray(payload)) {
                    console.error('Invalid payload for STATS_ACTIONS.UPDATE: expected array', payload);
                    return state;
                }
                
                log.reducer.log('statsReducer - UPDATE - Payload:', payload);
                log.reducer.log('statsReducer - État actuel:', state);
                
                let hasChanges = false;
                const nextState = { ...state };
                
                for (const element of payload) {
                    // Validation de la structure de l'élément
                    if (!Array.isArray(element) || element.length < 3 ||
                        typeof element[0] !== 'string' ||
                        (element[1] !== STATS_UPDATE_TYPE.INCREMENT && element[1] !== STATS_UPDATE_TYPE.DECREMENT) ||
                        typeof element[2] !== 'number' || isNaN(element[2])) {
                        console.error('Invalid stat update element:', element);
                        continue;
                    }
                    
                    const [statName, updateType, value, maxValue] = element;
                    
                    // Vérification que la stat existe dans l'état initial
                    if (!(statName in INITIAL_STATE_STATE)) {
                        console.error(`Invalid stat name: ${statName}`);
                        continue;
                    }
                    
                    const currentValue = nextState[statName];
                    let newValue;
                    
                    // Application de la mise à jour
                    if (updateType === STATS_UPDATE_TYPE.INCREMENT) {
                        newValue = currentValue + value;
                        log.reducer.log(`statsReducer - Incrémentation de ${statName}: ${currentValue} + ${value} = ${newValue}`);
                    } else {
                        newValue = currentValue - value;
                        log.reducer.log(`statsReducer - Décrémentation de ${statName}: ${currentValue} - ${value} = ${newValue}`);
                    }
                    
                    // Application des limites si spécifiées
                    if (maxValue !== undefined && typeof maxValue === 'number') {
                        if (updateType === STATS_UPDATE_TYPE.INCREMENT) {
                            newValue = Math.min(newValue, maxValue);
                        } else {
                            newValue = Math.max(newValue, maxValue);
                        }
                    }
                    
                    // Vérification des changements
                    if (newValue !== currentValue) {
                        log.reducer.log(`statsReducer - Mise à jour de ${statName}: ${currentValue} -> ${newValue}`);
                        nextState[statName] = newValue;
                        hasChanges = true;
                    } else {
                        log.reducer.log(`statsReducer - Aucun changement pour ${statName} (${newValue} === ${currentValue})`);
                    }
                }
                
                return hasChanges ? nextState : state;
            } catch (error) {
                console.error('Error in STATS_ACTIONS.UPDATE:', error);
                return state;
            }
        }
        case STATS_ACTIONS.SET: {
            try {
                const { payload } = action;
                
                // Validation du payload
                if (!Array.isArray(payload)) {
                    console.error("Invalid payload for STATS_ACTIONS.SET: expected array");
                    return state;
                }
                
                let hasChanges = false;
                const nextState = { ...state };
                
                for (const [key, value] of payload) {
                    // Validation de la clé et de la valeur
                    if (typeof key !== 'string' || !(key in INITIAL_STATE_STATE)) {
                        console.error(`Invalid attribute type by STATS_ACTIONS.SET: ${key}`);
                        continue;
                    }
                    
                    // Vérification du type de la valeur
                    const expectedType = typeof INITIAL_STATE_STATE[key];
                    if (typeof value !== expectedType) {
                        console.error(`Invalid value type for ${key}: expected ${expectedType}, got ${typeof value}`);
                        continue;
                    }
                    
                    // Vérification des changements
                    if (nextState[key] !== value) {
                        nextState[key] = value;
                        hasChanges = true;
                    }
                }
                
                return hasChanges ? nextState : state;
                
            } catch (error) {
                console.error('Error in STATS_ACTIONS.SET reducer:', error);
                return state;
            }
        }
        default:
            // Cas par défaut pour les actions non gérées
            return state;
    }
}

// ----------------------------------------------------------------------------
/**
 * Réducteur pour gérer l'inventaire du héros
 * @param {Object} state - État actuel de l'inventaire
 * @param {Object} action - Action à traiter
 * @returns {Object} Nouvel état de l'inventaire
 */
export const inventoryReducer = (state = {}, action = {}) => {
    // log.reducer.log('inventoryReducer', state, action)
    switch (action.type) {
        case INVENTORY_ACTIONS.USE_ITEM_BY_SLOT_INDEX: {
            try {
                const { slotIndex } = action.payload;
                
                // Vérification de la validité de l'index et de l'item
                if (typeof slotIndex !== 'number' || !(slotIndex in state) || !state[slotIndex]) {
                    console.error('Invalid slot index or empty slot:', slotIndex);
                    return state;
                }
                
                const currentItem = state[slotIndex];
                const nextQuantity = currentItem.quantity - 1;

                if (nextQuantity <= 0) {
                    // Créer un nouvel état sans l'item
                    const newState = { ...state };
                    delete newState[slotIndex];
                    return newState;
                } else {
                    // Mettre à jour la quantité
                    return {
                        ...state,
                        [slotIndex]: {
                            ...currentItem,
                            quantity: nextQuantity
                        }
                    };
                }
            } catch (error) {
                console.error('Error in INVENTORY_ACTIONS.USE_ITEM_BY_SLOT_INDEX:', error);
                return state;
            }
        }
        case INVENTORY_ACTIONS.ADD_ITEM_QUANTITY_BY_SLOT_INDEX: {
            try {
                // Vérification de la présence du payload
                if (!action.payload || typeof action.payload !== 'object') {
                    console.error('Invalid payload for ADD_ITEM_QUANTITY_BY_SLOT_INDEX:', action.payload);
                    return state;
                }
                
                const { slotIndex, quantity, idItem } = action.payload;
                
                // Validation des entrées
                if (typeof slotIndex !== 'number' || slotIndex < 0) {
                    console.error('Invalid slot index:', slotIndex);
                    return state;
                }
                
                if (typeof quantity !== 'number' || quantity <= 0 || !Number.isInteger(quantity)) {
                    console.error('Invalid quantity:', quantity);
                    return state;
                }
                
                if (!idItem) {
                    // Ajout de quantité à un item existant
                    if (!state[slotIndex]) {
                        console.error('Cannot add quantity to non-existent item at slot:', slotIndex);
                        return state;
                    }
                    
                    // Vérification de la structure de l'item existant
                    const currentItem = state[slotIndex];
                    if (!currentItem || typeof currentItem !== 'object' || 
                        typeof currentItem.quantity !== 'number' || currentItem.quantity <= 0) {
                        console.error('Invalid item structure at slot:', slotIndex, currentItem);
                        return state;
                    }
                    
                    // Calcul de la nouvelle quantité en évitant les dépassements
                    const newQuantity = currentItem.quantity + quantity;
                    if (newQuantity > Number.MAX_SAFE_INTEGER) {
                        console.error('Quantity overflow at slot:', slotIndex);
                        return state;
                    }
                    
                    return {
                        ...state,
                        [slotIndex]: {
                            ...currentItem,
                            quantity: newQuantity
                        }
                    };  
                } else {
                    // Ajout d'un nouvel item
                    if (typeof idItem !== 'string') {
                        console.error('Invalid item ID type:', typeof idItem);
                        return state;
                    }
                    
                    const item = ITEMS_LIST[idItem];
                    if (!item) {
                        console.error('Item not found in ITEMS_LIST:', idItem);
                        return state;
                    }
                    
                    // Vérification de la quantité maximale
                    const safeQuantity = Math.min(
                        Math.max(1, Math.floor(quantity)), // Quantité minimale de 1
                        Number.MAX_SAFE_INTEGER
                    );
                    
                    return {
                        ...state,
                        [slotIndex]: {
                            ...item, // Copie des propriétés de l'item
                            quantity: safeQuantity
                        }
                    }; 
                }
            } catch (error) {
                console.error('Error in INVENTORY_ACTIONS.ADD_ITEM_QUANTITY_BY_SLOT_INDEX:', {
                    error: error.message,
                    stack: error.stack,
                    action: action
                });
                return state;
            }
        }
        
        default:
            // Cas par défaut pour les actions non gérées
            return state;
    }
}